/* eslint-disable id-length */
import { TooltipModel } from "chart.js";
import { Fragment, useEffect } from "react";
import { take } from "lodash";

import { Card, CardSection, CardChart, CardLabel } from "../Card";
import LineChart from "../LineChart";
import { useChart } from "app/components/shared/Chart/chart";
import { TimePeriod } from "../Renderer";
import { Tooltip, TooltipLabel, TooltipValue } from "../Tooltip";
import { ChartLegend } from "../ChartLegend";
import { useChartData } from "./hooks";
import {
  calculateMin,
  calculateMax,
  getLatestValue,
  getTimePeriodString,
  calculateSum,
  Scope,
} from "./utils";
import { stackedChart, chartOptionsForScope } from "../chartOptions";

interface DataPoint {
  x: number;
  active: number;
  percentage: number;
  running_jobs: number;
}

interface Props {
  scope: Scope;
  dataset: {
    label: string;
    data: DataPoint[];
  }[];
  timePeriod: TimePeriod;
}

export function RunningJobs({ scope, dataset, timePeriod }: Props) {
  const { chartRef, updateData } = useChart<"line", DataPoint>();

  const data = useChartData({
    datasets: dataset,
    orderDatasetBy: ({ data }) => calculateMax(data, "running_jobs"),
    orderDirection: "asc",
    colorsOffset: 3,
  });

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex flex-col grow gap-3 min-w-0">
        <CardSection>
          <CardLabel description={getTimePeriodString(timePeriod)}>
            Running Jobs
          </CardLabel>
        </CardSection>

        <CardChart>
          <LineChart
            ref={chartRef}
            data={data}
            tooltip={ChartTooltip}
            options={{
              ...chartOptionsForScope(scope, {
                [Scope.Organization]: stackedChart,
                [Scope.Cluster]: stackedChart,
              }),
              parsing: { yAxisKey: "running_jobs", xAxisKey: "x" },
            }}
          />
        </CardChart>
      </div>
      <ChartLegend
        reverse={true}
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          Min: (_, index) => calculateMin(dataset[index].data, "running_jobs"),
          Max: (_, index) => calculateMax(dataset[index].data, "running_jobs"),
          "Running Jobs": (_, index) =>
            getLatestValue(dataset[index].data, "running_jobs"),
        }}
      />
    </Card>
  );
}

const ChartTooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const totalRunning = calculateSum(
    tooltip.dataPoints.map(({ parsed }) => parsed),
  );

  const visibleDataPoints = take(tooltip.dataPoints, 8);
  const hiddenDataPoints = tooltip.dataPoints.length - 8;

  return (
    <Tooltip tooltip={tooltip}>
      {visibleDataPoints.map(({ dataset, parsed }) => (
        <Fragment key={dataset.label}>
          <TooltipLabel color={dataset.borderColor as string}>
            {dataset.label}
          </TooltipLabel>
          <TooltipValue>{parsed.y}</TooltipValue>
        </Fragment>
      ))}

      <TooltipLabel>Total</TooltipLabel>
      <TooltipValue>{totalRunning}</TooltipValue>

      {hiddenDataPoints > 0 && (
        <span className="col-span-2 text-[11px] font-medium text-charcoal-300">
          ({hiddenDataPoints} not shown)
        </span>
      )}
    </Tooltip>
  );
};
